import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '-';
  if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  if (typeof d === 'string' && d.startsWith('/Date(')) {
    const ms = parseInt(d.replace(/\/Date\((\d+)[^)]*\)\//, '$1'));
    if (!isNaN(ms)) {
      const dt = new Date(ms);
      return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
    }
  }
  const parsed = new Date(d);
  if (isNaN(parsed)) return '-';
  return `${String(parsed.getDate()).padStart(2, '0')}/${String(parsed.getMonth() + 1).padStart(2, '0')}/${parsed.getFullYear()}`;
};
const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const PRIMARY     = '#1c2266';
const PRIMARY_MID = '#3f51b5';
const PRIMARY_LT  = '#e8eaf6';
const ITEM_BG     = '#f5f7ff';

const th  = { padding: '8px 10px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td  = { padding: '6px 10px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '6px 10px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const StaffSaleReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [search, setSearch]     = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'SaleReportApi',
        `/StaffSaleReports/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res?.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch staff sale report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  // Group: Salesman → Bills → Items (flat rows for rendering)
  // Each entry in `rows` array is either a bill-header row or an item row
  const { tableRows, grandTotal } = useMemo(() => {
    // 1. Group raw data into salesmen → bills → items
    const smMap = new Map();
    data.forEach(row => {
      if (!smMap.has(row.SalesManId)) {
        smMap.set(row.SalesManId, { SalesManId: row.SalesManId, SalesManName: row.SalesManName, bills: new Map() });
      }
      const sm = smMap.get(row.SalesManId);
      if (!sm.bills.has(row.SaleId)) {
        sm.bills.set(row.SaleId, {
          SaleId: row.SaleId, BillDate: row.BillDate, BillNo: row.BillNo,
          CustomerName: row.CustomerName, ContactNumber: row.ContactNumber,
          BillTotal: row.BillTotal, items: [],
        });
      }
      if (row.ItemName) {
        sm.bills.get(row.SaleId).items.push({
          ItemId: row.ItemId, ItemName: row.ItemName,
          Quantity: row.Quantity, Rate: row.Rate, Amount: row.Amount,
          DiscountAmount: row.DiscountAmount, TaxableAmount: row.TaxableAmount,
          CGSTAmount: row.CGSTAmount, SGSTAmount: row.SGSTAmount,
          IGSTAmount: row.IGSTAmount, ItemTotal: row.ItemTotal,
        });
      }
    });

    // 2. Apply search filter
    const q = search.trim().toLowerCase();
    const filtered = [];
    smMap.forEach(sm => {
      const bills = Array.from(sm.bills.values()).filter(b =>
        !q ||
        (sm.SalesManName  || '').toLowerCase().includes(q) ||
        (b.BillNo         || '').toLowerCase().includes(q) ||
        (b.CustomerName   || '').toLowerCase().includes(q) ||
        (b.ContactNumber  || '').toLowerCase().includes(q)
      );
      if (bills.length) filtered.push({ ...sm, bills });
    });

    // 3. Flatten into render rows
    let billSerial = 0;
    let total = 0;
    const rows = [];

    filtered.forEach(sm => {
      sm.bills.forEach((b, bi) => {
        billSerial++;
        total += parseFloat(b.BillTotal) || 0;
        // Bill header row — show salesman name only on first bill of each salesman
        rows.push({
          type: 'bill',
          key: `bill_${b.SaleId}`,
          serial: billSerial,
          showSalesman: bi === 0,         // ← only first bill shows salesman
          salesManName: sm.SalesManName,
          billNo: b.BillNo,
          billDate: b.BillDate,
          customerName: b.CustomerName,
          contactNumber: b.ContactNumber,
          billTotal: b.BillTotal,
          isEven: billSerial % 2 === 0,
        });
        // Item rows (always visible, no collapse)
        b.items.forEach((it, j) => {
          rows.push({
            type: 'item',
            key: `item_${b.SaleId}_${j}`,
            serial: j + 1,
            ...it,
          });
        });
      });
    });

    return { tableRows: rows, grandTotal: total };
  }, [data, search]);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'StaffSaleReport', 0);
    if (!tableRows.length) return;
    const colHeaders = [
      '#', 'Salesman', 'Bill No', 'Date', 'Customer', 'Contact', 'Bill Total',
      'Item', 'Qty', 'Rate', 'Amount', 'Discount', 'Taxable', 'CGST', 'SGST', 'IGST', 'Item Total',
    ];
    const rows = [];
    tableRows.forEach(r => {
      if (r.type === 'bill') {
        rows.push([
          r.serial,
          r.showSalesman ? (r.salesManName || '') : '',
          r.billNo || '', fmtDate(r.billDate),
          r.customerName || '', r.contactNumber || '',
          parseFloat(r.billTotal) || 0,
          '', '', '', '', '', '', '', '', '', '',
        ]);
      } else {
        rows.push([
          r.serial, '', '', '', '', '', '',
          r.ItemName || '',
          parseFloat(r.Quantity)       || 0,
          parseFloat(r.Rate)           || 0,
          parseFloat(r.Amount)         || 0,
          parseFloat(r.DiscountAmount) || 0,
          parseFloat(r.TaxableAmount)  || 0,
          parseFloat(r.CGSTAmount)     || 0,
          parseFloat(r.SGSTAmount)     || 0,
          parseFloat(r.IGSTAmount)     || 0,
          parseFloat(r.ItemTotal)      || 0,
        ]);
      }
    });
    await exportStyledExcel({
      reportTitle: 'STAFF SALE REPORT',
      sheetName:   'Staff Sale Report',
      fileName:    `StaffSaleReport_${fromDate}_${toDate}.xlsx`,
      companyName, username, fromDate, toDate,
      headers: colHeaders,
      rows,
    });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading staff sale report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* ── Toolbar ── */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d3494 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Staff Sale Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />

          {searchOpen ? (
            <>
              <TextField
                autoFocus size="small"
                placeholder="Search salesman, customer, bill..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search
                    ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment>
                    : null,
                }}
                sx={{
                  flexShrink: 0, width: 200,
                  '& .MuiOutlinedInput-root': {
                    height: 29, fontSize: 12, borderRadius: '8px', color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
                  },
                  '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 },
                }}
              />
              <Tooltip title="Close search" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!tableRows.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* ── Table ── */}
        <div className="report-table-scroll" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1100 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              {/* Bill-level columns */}
              <tr style={{ background: PRIMARY_MID, color: '#fff' }}>
                <th style={th}>#</th>
                <th style={th}>Salesman</th>
                <th style={th}>Bill No</th>
                <th style={th}>Date</th>
                <th style={th}>Customer</th>
                <th style={th}>Contact</th>
                <th style={{ ...th, textAlign: 'right' }}>Bill Total</th>
                {/* Item columns */}
                <th style={{ ...th, borderLeft: '2px solid rgba(255,255,255,0.3)' }}>Item Name</th>
                <th style={{ ...th, textAlign: 'right' }}>Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Rate</th>
                <th style={{ ...th, textAlign: 'right' }}>Discount</th>
                <th style={{ ...th, textAlign: 'right' }}>Taxable</th>
                <th style={{ ...th, textAlign: 'right' }}>CGST</th>
                <th style={{ ...th, textAlign: 'right' }}>SGST</th>
                <th style={{ ...th, textAlign: 'right' }}>IGST</th>
                <th style={{ ...th, textAlign: 'right' }}>Item Total</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.length === 0 ? (
                <tr>
                  <td colSpan={16} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click refresh'}
                  </td>
                </tr>
              ) : (
                tableRows.map(r => {
                  if (r.type === 'bill') {
                    return (
                      <tr key={r.key} style={{ background: r.isEven ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #e2e8f0' }}>
                        <td style={td}>{r.serial}</td>
                        {/* Salesman: shown only on first bill per salesman */}
                        <td style={{ ...td, fontWeight: 600, color: PRIMARY }}>
                          {r.showSalesman ? r.salesManName || '-' : ''}
                        </td>
                        <td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{r.billNo || '-'}</td>
                        <td style={td}>{fmtDate(r.billDate)}</td>
                        <td style={{ ...td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.customerName || '-'}</td>
                        <td style={{ ...td, color: '#64748b' }}>{r.contactNumber || '-'}</td>
                        <td style={{ ...tdR, fontWeight: 700 }}>{fmt(r.billTotal)}</td>
                        {/* Empty item cells on bill row */}
                        <td style={{ ...td, borderLeft: '2px solid #e2e8f0' }} colSpan={9}></td>
                      </tr>
                    );
                  }

                  // Item row
                  return (
                    <tr key={r.key} style={{ background: ITEM_BG, borderBottom: '1px solid #ede9fe' }}>
                      <td style={{ ...td, color: '#6b7280', fontSize: 11 }}>{r.serial}</td>
                      <td colSpan={6} style={{ ...td, borderLeft: '0' }}></td>
                      {/* Item columns */}
                      <td style={{ ...td, fontWeight: 500, color: '#1e293b', borderLeft: '2px solid #e2e8f0', paddingLeft: 18 }}>{r.ItemName}</td>
                      <td style={tdR}>{fmtQty(r.Quantity)}</td>
                      <td style={tdR}>{fmt(r.Rate)}</td>
                      <td style={{ ...tdR, color: '#dc2626' }}>{fmt(r.DiscountAmount)}</td>
                      <td style={tdR}>{fmt(r.TaxableAmount)}</td>
                      <td style={tdR}>{fmt(r.CGSTAmount)}</td>
                      <td style={tdR}>{fmt(r.SGSTAmount)}</td>
                      <td style={tdR}>{fmt(r.IGSTAmount)}</td>
                      <td style={{ ...tdR, fontWeight: 600 }}>{fmt(r.ItemTotal)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* ── Grand Total Footer ── */}
            {tableRows.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={6} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Grand Total</td>
                  <td style={tdR}>{fmt(grandTotal)}</td>
                  <td colSpan={9} style={td}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default StaffSaleReport;
